import { useEffect, useMemo, useCallback, useState } from "react"
import { useCommander } from "./context"
import type { Command, CommandKey, CommandCategory } from "../types"

// ============================================================================
// TYPES
// ============================================================================

interface UseCustomCommandProps<TInput = any, TOutput = any> {
  key: string
  label?: string
  description?: string
  category?: CommandCategory
  tags?: string[]
  icon?: string
  shortcut?: string
  when?: () => boolean | Promise<boolean>
  handle: (input?: TInput) => Promise<TOutput>
  timeout?: number
  priority?: number
  owner?: string
  searchKeywords?: string[]
}

interface CustomCommandInvoker<TInput, TOutput> {
  key: CommandKey
  exists: () => boolean
  isAvailable: () => Promise<boolean>
  invoke: (
    input?: TInput,
    source?: "palette" | "shortcut" | "api",
  ) => Promise<TOutput>
  attempt: (
    input?: TInput,
    source?: "palette" | "shortcut" | "api",
  ) => Promise<{
    success: boolean
    result?: TOutput
    error?: any
    command: CommandKey
  }>
  getCommand: () => Command<TInput, TOutput> | undefined
}

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Hook to register a temporary command that exists only while the component is mounted
 *
 * @param props Command configuration
 * @returns Invoker object for executing the command
 *
 * @example
 * ```tsx
 * function FileEditor({ file }) {
 *   const saveInvoker = useCustomCommand({
 *     key: `file:save:${file.id}`,
 *     label: `Save ${file.name}`,
 *     icon: '💾',
 *     shortcut: 'ctrl+s',
 *     handle: async () => saveFile(file)
 *   });
 *
 *   // Command is automatically registered and appears in Command Palette
 *   // Execute programmatically if needed:
 *   const handleSave = () => saveInvoker.invoke();
 * }
 * ```
 */
export function useCustomCommand<TInput = any, TOutput = any>(
  props: UseCustomCommandProps<TInput, TOutput>,
): CustomCommandInvoker<TInput, TOutput> {
  const commander = useCommander()

  const {
    key,
    label,
    description,
    category = "custom",
    tags = [],
    icon,
    shortcut,
    when,
    handle,
    timeout,
    priority,
    owner = "temporary",
    searchKeywords = [],
  } = props

  // Memoize command to avoid unnecessary re-registrations
  const command = useMemo<Command<TInput, TOutput>>(
    () => ({
      key: key as CommandKey,
      label: label || key,
      description,
      category,
      tags,
      icon,
      shortcut,
      when,
      handle,
      timeout,
      priority,
      owner,
      searchKeywords,
    }),
    [
      key,
      label,
      description,
      category,
      tags.join(","),
      icon,
      shortcut,
      when,
      handle,
      timeout,
      priority,
      owner,
      searchKeywords.join(","),
    ],
  )

  // Register command on mount, remove on unmount
  useEffect(() => {
    try {
      const registeredCommand = commander.add(command)

      // Return cleanup function
      return () => {
        commander.remove(registeredCommand)
      }
    } catch (error) {
      console.error(`Failed to register custom command "${key}":`, error)
    }
  }, [commander, command, key])

  // Create invoker object
  const invoker = useMemo<CustomCommandInvoker<TInput, TOutput>>(() => {
    const commandKey = key as CommandKey

    return {
      key: commandKey,

      exists: () => commander.has(commandKey),

      isAvailable: async () => {
        const cmd = commander.getCommand(commandKey)
        return cmd ? commander.isCommandAvailable(cmd) : false
      },

      invoke: async (
        input?: TInput,
        source: "palette" | "shortcut" | "api" = "api",
      ) => {
        return commander.invoke<TOutput>(commandKey, input, source)
      },

      attempt: async (
        input?: TInput,
        source: "palette" | "shortcut" | "api" = "api",
      ) => {
        return commander.attempt<TOutput>(commandKey, input, source)
      },

      getCommand: () =>
        commander.getCommand(commandKey) as
          | Command<TInput, TOutput>
          | undefined,
    }
  }, [commander, key])

  return invoker
}

// ============================================================================
// SPECIALIZED HOOKS
// ============================================================================

/**
 * Hook for creating a simple action command (no input/output)
 */
export function useAction(
  key: string,
  label: string,
  action: () => Promise<void> | void,
  options: Partial<
    Omit<UseCustomCommandProps<void, void>, "key" | "label" | "handle">
  > = {},
): CustomCommandInvoker<void, void> {
  return useCustomCommand<void, void>({
    key,
    label,
    handle: async () => {
      await action()
    },
    ...options,
  })
}

/**
 * Hook for creating a toggle command
 */
export function useToggleCommand(
  key: string,
  label: string,
  isActive: boolean,
  onToggle: (newState: boolean) => Promise<void> | void,
  options: Partial<
    Omit<UseCustomCommandProps<void, boolean>, "key" | "label" | "handle">
  > = {},
): CustomCommandInvoker<void, boolean> {
  return useCustomCommand<void, boolean>({
    key,
    label: `${isActive ? "Disable" : "Enable"} ${label}`,
    icon: isActive ? "🔴" : "🟢",
    handle: async () => {
      const newState = !isActive
      await onToggle(newState)
      return newState
    },
    ...options,
  })
}

/**
 * Hook for creating a modal command that returns a promise
 */
export function useModalCommand<TInput, TOutput>(
  key: string,
  label: string,
  openModal: (input?: TInput) => Promise<TOutput>,
  options: Partial<
    Omit<UseCustomCommandProps<TInput, TOutput>, "key" | "label" | "handle">
  > = {},
): CustomCommandInvoker<TInput, TOutput> {
  return useCustomCommand<TInput, TOutput>({
    key,
    label,
    handle: openModal,
    category: "tools",
    ...options,
  })
}

/**
 * Hook for creating a navigation command
 */
export function useNavigationCommand(
  key: string,
  label: string,
  path: string,
  navigate: (path: string, state?: any) => void,
  options: Partial<
    Omit<UseCustomCommandProps<void, void>, "key" | "label" | "handle">
  > = {},
): CustomCommandInvoker<void, void> {
  return useCustomCommand<void, void>({
    key,
    label,
    category: "view",
    icon: "🔗",
    handle: async () => {
      navigate(path)
    },
    ...options,
  })
}

/**
 * Hook for creating contextual commands based on data
 */
export function useContextualCommands<TData>(
  data: TData[],
  commandFactory: (item: TData, index: number) => UseCustomCommandProps,
) {
  const commander = useCommander()
  const invokers: CustomCommandInvoker<any, any>[] = []

  useEffect(() => {
    const registeredCommands: Command[] = []

    // Register commands for each data item
    data.forEach((item, index) => {
      try {
        const commandProps = commandFactory(item, index)
        const command: Command = {
          key: commandProps.key as CommandKey,
          label: commandProps.label || commandProps.key,
          description: commandProps.description,
          category: commandProps.category || "custom",
          tags: commandProps.tags || [],
          icon: commandProps.icon,
          shortcut: commandProps.shortcut,
          when: commandProps.when,
          handle: commandProps.handle,
          timeout: commandProps.timeout,
          priority: commandProps.priority,
          owner: commandProps.owner || "contextual",
          searchKeywords: commandProps.searchKeywords,
        }

        const registeredCommand = commander.add(command)
        registeredCommands.push(registeredCommand)
      } catch (error) {
        console.error(
          `Failed to register contextual command for item ${index}:`,
          error,
        )
      }
    })

    // Cleanup all registered commands
    return () => {
      registeredCommands.forEach((command) => {
        commander.remove(command)
      })
    }
  }, [data, commandFactory, commander])

  return {
    count: data.length,
    refresh: useCallback(() => {
      // Force re-registration by updating the effect dependencies
      // This is handled automatically by the useEffect dependencies
    }, []),
  }
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to check if a custom command exists and is available
 */
export function useCommandStatus(key: CommandKey) {
  const commander = useCommander()
  const [isExecuting, setIsExecuting] = useState(false)
  const [lastResult, setLastResult] = useState<any>(undefined)
  const [lastError, setLastError] = useState<any>(undefined)

  const exists = commander.has(key)

  const checkAvailability = useCallback(async () => {
    const command = commander.getCommand(key)
    return command ? commander.isCommandAvailable(command) : false
  }, [commander, key])

  useEffect(() => {
    const executingListener = commander.listen(
      "command:executing",
      (context) => {
        if (context.command.key === key) {
          setIsExecuting(true)
          setLastError(undefined)
        }
      },
    )

    const completedListener = commander.listen(
      "command:completed",
      (context, result) => {
        if (context.command.key === key) {
          setIsExecuting(false)
          setLastResult(result)
        }
      },
    )

    const failedListener = commander.listen(
      "command:failed",
      (context, error) => {
        if (context.command.key === key) {
          setIsExecuting(false)
          setLastError(error)
        }
      },
    )

    return () => {
      commander.removeListener(executingListener)
      commander.removeListener(completedListener)
      commander.removeListener(failedListener)
    }
  }, [commander, key])

  return {
    exists,
    checkAvailability,
    isExecuting,
    lastResult,
    lastError,
  }
}

/**
 * Hook to listen for custom command events
 */
export function useCommandEvents(
  key: CommandKey,
  handlers: {
    onExecuting?: (context: any) => void
    onExecuted?: (context: { result: any }) => void
    onError?: (error: any) => void
  },
) {
  const commander = useCommander()

  useEffect(() => {
    const listeners: any[] = []

    if (handlers.onExecuting) {
      const listener = commander.listen("command:executing", (context) => {
        if (context.command.key === key) {
          handlers.onExecuting!(context)
        }
      })
      listeners.push(listener)
    }

    if (handlers.onExecuted) {
      const listener = commander.listen(
        "command:completed",
        (context, result) => {
          if (context.command.key === key) {
            handlers.onExecuted!({ result })
          }
        },
      )
      listeners.push(listener)
    }

    if (handlers.onError) {
      const listener = commander.listen("command:failed", (context, error) => {
        if (context.command.key === key) {
          handlers.onError!(error)
        }
      })
      listeners.push(listener)
    }

    return () => {
      listeners.forEach((listener) => {
        commander.removeListener(listener)
      })
    }
  }, [
    commander,
    key,
    handlers.onExecuting,
    handlers.onExecuted,
    handlers.onError,
  ])
}
